import React, { useState } from "react";
import "../styles/wc.css";

export default function AttributionBar({
    daysEffect = 0,
    growthEffect = 0,
    companyName = "this company",
    dsoDelta = 0,
    dioDelta = 0,
    dpoDelta = 0,
    cccDelta = 0
}) {
    const [showExplanation, setShowExplanation] = useState(false);
    const total = Math.abs(daysEffect) + Math.abs(growthEffect) || 1;
    const daysPct = (Math.abs(daysEffect) / total) * 100;
    const growthPct = (Math.abs(growthEffect) / total) * 100;

    // Determine if effects are positive (freeing cash) or negative (tying up cash)
    const daysImpact = daysEffect > 0 ? "released cash" : "consumed cash";
    const growthImpact = growthEffect > 0 ? "released cash" : "consumed cash";

    const daysLabel = `${daysEffect >= 0 ? "+" : ""}${daysEffect.toFixed(0)} Cr ${daysImpact}`;
    const growthLabel = `${growthEffect >= 0 ? "+" : ""}${growthEffect.toFixed(0)} Cr ${growthImpact}`;
    const netChange = daysEffect + growthEffect;
    const netLabel = `Net: ${netChange >= 0 ? "+" : ""}${netChange.toFixed(0)} Cr ${netChange >= 0 ? "cash released" : "cash consumed"}`;

    // More intuitive descriptions
    const efficiencyDesc = daysEffect >= 0
        ? "Improved cycle efficiency (collecting faster, reducing inventory, or extending payment terms)"
        : "Cycle efficiency declined (slower collections, higher inventory, or faster supplier payments)";

    const scaleDesc = growthEffect < 0
        ? "Business growth required more working capital investment"
        : "Despite growth, working capital improved";

    // Find the dominant change to highlight
    const getDominantChange = () => {
        const deltas = [
            { metric: 'DPO', value: dpoDelta, label: 'payment terms to suppliers' },
            { metric: 'DSO', value: dsoDelta, label: 'customer collection time' },
            { metric: 'DIO', value: dioDelta, label: 'inventory holding period' }
        ];

        const maxDelta = deltas.reduce((max, curr) =>
            Math.abs(curr.value) > Math.abs(max.value) ? curr : max
        );

        return maxDelta;
    };

    const dominantChange = getDominantChange();
    const changeDirection = dominantChange.value > 0 ? 'increased' : 'decreased';

    return (
        <div className="wc-attr">
            <div className="wc-attr__title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>💰 Working Capital Attribution</span>
                        <button
                            onClick={() => setShowExplanation(!showExplanation)}
                            style={{
                                background: 'none',
                                border: '1px solid var(--border)',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                color: 'var(--muted)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            title="Click for detailed explanation"
                        >
                            ?
                        </button>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 'normal' }}>3-Year Trend Analysis</span>
                </div>
                <span style={{ fontSize: '14px', fontWeight: 'bold', color: netChange >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                    {netLabel}
                </span>
            </div>

            {showExplanation && (
                <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: 'var(--bg-3)',
                    borderLeft: '3px solid var(--brand-2)',
                    borderRadius: '4px',
                    fontSize: '12px',
                    lineHeight: '1.6'
                }}>
                    <div style={{
                        marginBottom: '12px',
                        padding: '8px',
                        backgroundColor: 'var(--bg-2)',
                        borderRadius: '4px',
                        fontWeight: '500'
                    }}>
                        🔍 <strong>Key Concept:</strong> This breaks down ONE working capital change into TWO opposing forces (like a tug-of-war).
                        The net shows which force won.
                    </div>

                    <div style={{
                        marginBottom: '12px',
                        padding: '10px',
                        backgroundColor: 'var(--card-bg)',
                        border: '1px solid var(--border)',
                        borderRadius: '6px'
                    }}>
                        <strong style={{ fontSize: '13px', color: 'var(--text-1)' }}>📌 Real Example ({companyName}):</strong>
                        <ul style={{ marginTop: '8px', marginLeft: '20px', fontSize: '12px', lineHeight: '1.8' }}>
                            <li>
                                <strong>What actually happened:</strong> {dominantChange.metric} {changeDirection} by {Math.abs(dominantChange.value).toFixed(0)} days
                                {dsoDelta !== 0 && ` (DSO: ${dsoDelta > 0 ? '+' : ''}${dsoDelta.toFixed(0)}d)`}
                                {dioDelta !== 0 && ` (DIO: ${dioDelta > 0 ? '+' : ''}${dioDelta.toFixed(0)}d)`}
                                {dpoDelta !== 0 && ` (DPO: ${dpoDelta > 0 ? '+' : ''}${dpoDelta.toFixed(0)}d)`}
                            </li>
                            <li>
                                <strong>Efficiency effect:</strong> By {changeDirection === 'increased' && dominantChange.metric === 'DPO' ? 'extending' : 'changing'} {dominantChange.label},
                                the company {daysEffect > 0 ? 'freed up' : 'consumed'} <strong>₹{Math.abs(daysEffect).toFixed(0)} Cr</strong> that would otherwise be tied up in working capital
                            </li>
                            <li>
                                <strong>Growth effect:</strong> But because the business {growthEffect < 0 ? 'grew and needed more inventory/receivables' : 'managed growth efficiently'},
                                it {growthEffect < 0 ? 'consumed' : 'released'} <strong>₹{Math.abs(growthEffect).toFixed(0)} Cr</strong>
                            </li>
                            <li>
                                <strong>Net:</strong> {daysEffect > 0 && Math.abs(daysEffect) > Math.abs(growthEffect) ? 'Efficiency wins' : growthEffect > 0 ? 'Both contributed positively' : 'Growth pressure dominated'},
                                so <strong style={{ color: netChange >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                                    {netChange >= 0 ? '+' : ''}{netChange.toFixed(0)} Cr cash {netChange >= 0 ? 'released' : 'consumed'}
                                </strong> overall
                            </li>
                        </ul>
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                        <strong>📊 Operational Efficiency (Days Effect):</strong>
                        <div style={{ marginLeft: '8px', marginTop: '4px' }}>
                            "If we kept the same business size but improved our DSO/DIO/DPO ratios..."
                            <ul style={{ marginTop: '4px', marginLeft: '20px', fontSize: '11px' }}>
                                <li><strong>DSO ↓</strong> = Collect from customers faster → Cash released</li>
                                <li><strong>DIO ↓</strong> = Hold less inventory → Cash released</li>
                                <li><strong>DPO ↑</strong> = Pay suppliers slower → Cash released</li>
                            </ul>
                        </div>
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                        <strong>📈 Business Scale (Growth Effect):</strong>
                        <div style={{ marginLeft: '8px', marginTop: '4px' }}>
                            "If we kept the same efficiency ratios but just grew revenue/COGS..."
                            <ul style={{ marginTop: '4px', marginLeft: '20px', fontSize: '11px' }}>
                                <li>Larger sales = More receivables needed → Cash consumed</li>
                                <li>More production = More inventory needed → Cash consumed</li>
                                <li>Bigger operations = More payables too (helps offset)</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            <div className="wc-attr__bar" role="img" aria-label="Attribution stacked bar" style={{ marginTop: '8px' }}>
                <div
                    className={`wc-attr__segment ${daysEffect >= 0 ? 'wc-attr__segment--days-good' : 'wc-attr__segment--days'}`}
                    style={{ width: `${daysPct}%` }}
                    title={`${daysLabel} (${daysPct.toFixed(0)}% of total movement)`}
                />
                <div
                    className={`wc-attr__segment ${growthEffect >= 0 ? 'wc-attr__segment--growth-good' : 'wc-attr__segment--growth'}`}
                    style={{ width: `${growthPct}%` }}
                    title={`${growthLabel} (${growthPct.toFixed(0)}% of total movement)`}
                />
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px', textAlign: 'center' }}>
                Bar shows relative contribution: Efficiency <strong>{daysPct.toFixed(0)}%</strong> vs Growth <strong>{growthPct.toFixed(0)}%</strong> of total WC movement
            </div>
            <div className="wc-attr__legend" style={{ marginTop: '8px', fontSize: '13px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginBottom: '6px' }}>
                    <span style={{
                        display: 'inline-block',
                        width: '12px',
                        height: '12px',
                        marginTop: '2px',
                        backgroundColor: daysEffect >= 0 ? 'var(--success)' : 'var(--brand-2)',
                        borderRadius: '2px'
                    }}></span>
                    <div style={{ flex: 1 }}>
                        <div><strong>Operational Efficiency ({daysPct.toFixed(0)}%):</strong> {daysLabel}</div>
                        <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>{efficiencyDesc}</div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                    <span style={{
                        display: 'inline-block',
                        width: '12px',
                        height: '12px',
                        marginTop: '2px',
                        backgroundColor: growthEffect >= 0 ? 'var(--success)' : 'var(--muted)',
                        borderRadius: '2px'
                    }}></span>
                    <div style={{ flex: 1 }}>
                        <div><strong>Business Scale ({growthPct.toFixed(0)}%):</strong> {growthLabel}</div>
                        <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>{scaleDesc}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
